import * as React from 'react'
import './domain-list.scss'
import { Link } from 'react-router-dom'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { Button, FilterBar, StatusBadge, StatusBadgeProps } from '@pushly/aqe/lib/components'
import type { ActiveFilter, ButtonAction, FilterDefinition } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import {
    CopyOutlined,
    DeleteOutlined,
    EyeOutlined,
    GlobalOutlined,
    Loading3QuartersOutlined,
    QuestionCircleOutlined,
    WarningOutlined,
    BarChartOutlined,
} from '@ant-design/icons'
import { Flex, Form, Input, Modal, Table, Tag } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { DomainDto } from '@halo/dtos/domain.dto'
import { AccountService } from '@halo/services/account.service'
import { DomainService } from '@halo/services/domain.service'
import { AccountDto } from '@halo/dtos/account.dto'
import clone from 'clone-deep'
import { Flags } from '@pushly/aquarium-utils/lib/enums/flags'
import GenerateTestDomainModal from '@halo/components/generate-test-domain-modal/generate-test-domain-modal'
import { FormInstance, RuleObject } from 'antd/es/form'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { asCaslSubject, CurrentUserCan, ICaslSubject } from '@halo/stores/ability.state'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'
import { StatusType } from '@pushly/aqe/lib/enums'
import deepEqual from 'react-fast-compare'
import { NewDomainModal, NewDomainModalMode } from './new-domain-modal'

// Values are the server's `type` vocabulary. All three or none selected means no filter.
type DomainTypeFilter = 'standard' | 'demo' | 'template'

// ─── DomainList ───────────────────────────────────────────────────────────────

interface IDomainList {
    options: {
        account?: AccountDto
    }
    /**
     * Hide the New Domain button on the table header. Parent is expected to render it
     * elsewhere (e.g. in a page-level PageHeader.action). Defaults to false.
     */
    hideCreateButton?: boolean
}

interface IDomainListState {
    loading: boolean
    domains: DomainDto[]
    domainsMeta: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
    newDomainModalMode: NewDomainModalMode | null
    newDomainModalInitialTemplateId?: number
    newDomainModalInitialValues?: Partial<DomainDto>
    showNewTestDomainPrompt: boolean
    generateDomain: any
    statusFilter: string[]
    typeFilter: DomainTypeFilter[]
}

export class DomainList extends React.Component<IDomainList, IDomainListState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected domainService: DomainService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.domainService = Container.get(DomainService)

        this.state = {
            loading: true,
            domains: [],
            domainsMeta: {},
            page: 1,
            pageSize: 15,
            newDomainModalMode: null,
            newDomainModalInitialTemplateId: undefined,
            newDomainModalInitialValues: undefined,
            showNewTestDomainPrompt: false,
            generateDomain: {},
            statusFilter: [StatusType[StatusType.ACTIVE]],
            typeFilter: this.defaultTypeFilter(),
        }
    }

    // No type is selected by default, so every list shows each type the user can access. The server only
    // returns the demo and template records the user may read.
    protected defaultTypeFilter(): DomainTypeFilter[] {
        return []
    }

    public componentDidMount(): void {
        this.fetchDomains()
    }

    /**
     * Open the New Domain modal. Public so parent components rendering their own
     * primary action button (e.g. via PageHeader.action with hideCreateButton=true)
     * can drive the modal.
     */
    public openNewDomainModal(mode: NewDomainModalMode): void {
        this.setState({ newDomainModalMode: mode })
    }

    protected get isDemoOrg(): boolean {
        return this.props.options.account?.is_demo === true
    }

    protected get isTemplateOrg(): boolean {
        return this.props.options.account?.is_demo_template === true
    }

    protected get isInternalUser(): boolean {
        return this.appState.currentUser?.isInternalUser ?? false
    }

    public render() {
        let caslCreateSubject: SubjectEntity | ICaslSubject<SubjectEntity.DOMAIN> = SubjectEntity.DOMAIN
        if (this.props.options.account?.id) {
            caslCreateSubject = asCaslSubject(SubjectEntity.DOMAIN, {
                accountId: this.props.options.account.id,
            })
        }

        // Button label and behavior depends on org type:
        // - Template org → "Create Demo Template Domain" (internal only)
        // - Demo org → "Create Demo Domain" (internal only)
        // - Standard org → "New Domain" (standard CASL-gated)
        let createButton: React.ReactNode
        if (this.isTemplateOrg) {
            createButton = this.isInternalUser ? (
                <Button type="primary" onClick={() => this.setState({ newDomainModalMode: 'demo-template' })}>
                    Create Demo Template Domain
                </Button>
            ) : null
        } else if (this.isDemoOrg) {
            createButton = this.isInternalUser ? (
                <Button type="primary" onClick={() => this.setState({ newDomainModalMode: 'demo' })}>
                    Create Demo Domain
                </Button>
            ) : null
        } else {
            createButton = (
                <CurrentUserCan do={AbilityAction.CREATE} on={caslCreateSubject}>
                    <Button type="primary" onClick={() => this.setState({ newDomainModalMode: 'standard' })}>
                        New Domain
                    </Button>
                </CurrentUserCan>
            )
        }

        const filterDefinitions: FilterDefinition[] = [
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: StatusType[StatusType.ACTIVE], label: 'Active' },
                    { value: StatusType[StatusType.DISABLED], label: 'Disabled' },
                ],
            },
            {
                key: 'type',
                label: 'Type',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: 'standard', label: 'Standard' },
                    { value: 'demo', label: 'Demo' },
                    { value: 'template', label: 'Template' },
                ],
            },
        ]

        const getFilterBarValue = (): ActiveFilter[] => {
            const active: ActiveFilter[] = []
            if (this.state.statusFilter.length > 0) {
                active.push({ key: 'status', operator: 'in', value: this.state.statusFilter })
            }
            if (this.state.typeFilter.length > 0) {
                active.push({ key: 'type', operator: 'in', value: this.state.typeFilter })
            }
            return active
        }

        const handleFilterBarApply = (filters: ActiveFilter[]) => {
            const nextStatus = (filters.find((f) => f.key === 'status')?.value as string[]) ?? []
            const nextType = (filters.find((f) => f.key === 'type')?.value as DomainTypeFilter[]) ?? []
            this.applyFilters(nextStatus, nextType)
        }

        const tableHeader = (
            <Flex className="domain-list-header" gap={8} justify="space-between" align="center">
                <Flex flex={1} gap={8} align="center">
                    <Input.Search
                        className="domain-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Domain Name"
                        enterButton={true}
                        onSearch={this.searchDomains}
                    />
                    <FilterBar
                        filterDefinitions={filterDefinitions}
                        value={getFilterBarValue()}
                        onApply={handleFilterBarApply}
                        showInlineTags={false}
                        showInlineClearAll={false}
                        size="middle"
                    />
                </Flex>
                {!this.props.hideCreateButton && createButton && <Flex align="center">{createButton}</Flex>}
            </Flex>
        )

        return (
            <div className={classnames('domain-list')}>
                <Table
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    dataSource={this.state.domains}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={this.state.paginationLinks}
                            onPrev={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchDomains())
                            }}
                            onNext={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchDomains())
                            }}
                        />
                    )}
                    rowKey={(record) => record.id.toString()}
                    loading={this.state.loading && { indicator: <Loading3QuartersOutlined spin={true} /> }}
                    locale={{ emptyText: <AntdTableEmptyPlaceholder text="No Domains" icon={<GlobalOutlined />} /> }}
                >
                    <Table.Column className="id" title="ID" dataIndex="id" />
                    <Table.Column className="domain" title="Domain" render={this.renderDomainColumn} />
                    <Table.Column className="status" title="Status" render={this.renderStatus} />
                    <Table.Column className="actions" title="Actions" align="center" render={this.renderRowActions} />
                </Table>

                {/* Unified new domain modal */}
                {this.state.newDomainModalMode && (
                    <NewDomainModal
                        key={`new-domain-${this.state.newDomainModalMode}-${this.props.options.account?.id ?? '0'}-${
                            this.state.newDomainModalInitialTemplateId ?? '0'
                        }`}
                        mode={this.state.newDomainModalMode}
                        accountId={this.props.options.account?.id}
                        initialTemplateId={this.state.newDomainModalInitialTemplateId}
                        initialValues={this.state.newDomainModalInitialValues}
                        visible={true}
                        onOk={this.handleDomainCreation}
                        onCancel={() =>
                            this.setState({
                                newDomainModalMode: null,
                                newDomainModalInitialTemplateId: undefined,
                                newDomainModalInitialValues: undefined,
                            })
                        }
                    />
                )}

                <GenerateTestDomainModal
                    domain={this.state.generateDomain}
                    visible={this.state.showNewTestDomainPrompt}
                    onCancel={this.hideNewTestDomainPrompt}
                    onOk={this.handleTestDomainCreation}
                />
            </div>
        )
    }

    protected renderDomainColumn = (_: string, domain: DomainDto): React.ReactNode => {
        return (
            <TableRowEntityDisplay
                title={
                    <span>
                        <Link to={this.buildDomainUrl(domain)}>{domain.display_name}</Link>
                        {domain.is_demo_template && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>
                                Template
                            </Tag>
                        )}
                        {domain.is_demo && !domain.is_demo_template && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                                Demo
                            </Tag>
                        )}
                    </span>
                }
                status={!this.props.options.account?.id && domain.account_name}
                badges={domain.name}
            />
        )
    }

    protected renderStatus = (_: string, domain: DomainDto): React.ReactNode => {
        return <StatusBadge type="domain" status={domain.status as StatusBadgeProps['status']} expanded={true} />
    }

    protected renderRowActions = (_: any, domain: DomainDto): React.ReactNode => {
        const actions: ButtonAction[] = []

        const domainFlags = domain.flags ?? []
        const demoIdRgx = /DEMO_ID_(?<demo_id>[\d]+)/i
        const flagMatch = demoIdRgx.exec(domainFlags.join(','))
        const demoDomainId = flagMatch?.groups?.demo_id
        const isLegacyDemoDomain = domainFlags.includes(Flags.domain.DEMO.key) && !!demoDomainId
        const isNewDemoDomain = domain.is_demo === true
        const isProdDomain = domain.prod_domain_id === null || domain.prod_domain_id === undefined
        const canUpdateDomain = this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(SubjectEntity.DOMAIN, { ...domain, statusId: domain.status_id }),
        )

        if (canUpdateDomain) {
            if (isProdDomain && !isLegacyDemoDomain && !isNewDemoDomain) {
                actions.push({
                    key: 'generate-test-domain',
                    text: 'Generate Test Domain',
                    icon: <CopyOutlined />,
                    onClick: () => this.handleActionClick('generate-test-domain', domain, null as any),
                })
            }

            // Archive action for new-style demo domains (internal users only, not templates)
            if (isNewDemoDomain && !domain.is_demo_template && this.isInternalUser) {
                actions.push({
                    key: 'reclone',
                    text: 'Refresh Demo Domain',
                    icon: <GlobalOutlined />,
                    onClick: () => this.handleActionClick('reclone', domain, null as any),
                })
                actions.push({
                    key: 'reseed-stats',
                    text: 'Refresh Demo Stats',
                    icon: <BarChartOutlined />,
                    onClick: () => this.handleActionClick('reseed-stats', domain, null as any),
                })
                actions.push({
                    key: 'clone-demo',
                    text: 'Clone Demo Domain',
                    icon: <CopyOutlined />,
                    onClick: () => this.handleActionClick('clone-demo', domain, null as any),
                })
                actions.push({
                    key: 'archive-demo',
                    text: 'Archive',
                    icon: <DeleteOutlined />,
                    onClick: () => this.handleActionClick('archive-demo', domain, null as any),
                })
            }

            if (domain.status === 'ACTIVE') {
                actions.push({
                    key: 'disable',
                    text: 'Disable',
                    icon: <EyeOutlined />,
                    onClick: () => this.handleActionClick('disable', domain, null as any),
                })
            }

            if (domain.status === 'DISABLED') {
                actions.push({
                    key: 'enable',
                    text: 'Enable',
                    icon: <EyeOutlined />,
                    onClick: () => this.handleActionClick('enable', domain, null as any),
                })
            }
        }

        return (
            <Button
                size="small"
                altHref={this.buildDomainUrl(domain)}
                onClick={(ev) => this.handlePrimaryActionClick(domain, ev as React.MouseEvent<HTMLElement>)}
                actions={actions.length > 0 ? actions : undefined}
            >
                View
            </Button>
        )
    }

    protected buildDomainUrl(domain: DomainDto): string {
        let url = `/domains/${domain.id}`
        if (this.props.options.account?.id) {
            url = `/organizations/${this.props.options.account?.id}${url}`
        }
        return url
    }

    protected async jumpToDomain(domain: DomainDto): Promise<void> {
        interface IDomainRouteState {
            account: AccountDto
            domain?: DomainDto
        }
        const routeState: IDomainRouteState = { account: this.props.options.account }

        const fullDomainReq = await this.domainService.fetchById(domain.id)
        if (fullDomainReq.ok) {
            routeState.domain = fullDomainReq.data
        }

        this.appService.route(this.buildDomainUrl(domain), routeState)
    }

    protected showNewTestDomainPrompt = (domain: DomainDto) => {
        this.setState({ showNewTestDomainPrompt: true, generateDomain: clone(domain) })
    }

    protected hideNewTestDomainPrompt = () => this.setState({ showNewTestDomainPrompt: false, generateDomain: {} })

    protected handleDomainCreation = async (domainDto: Partial<DomainDto>): Promise<void> => {
        const { ok, data: domain } = await this.domainService.create(domainDto, { showLoadingScreen: false })
        if (!ok) {
            throw new Error('Failed to create domain.')
        }
        this.setState({ newDomainModalMode: null })
        await this.appState.abilityStore.refresh()
        this.jumpToDomain(domain)
    }

    protected handleTestDomainCreation = (domain: DomainDto): void => {
        this.jumpToDomain(domain)
    }

    protected handlePrimaryActionClick(domain: DomainDto, event?: React.MouseEvent<HTMLElement>): void {
        if (event?.metaKey) {
            window.open(this.buildDomainUrl(domain))
        } else {
            event?.preventDefault()
            this.jumpToDomain(domain)
        }
    }

    protected handleActionClick = (
        key: string | number,
        domain: DomainDto,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'reclone') {
            Modal.confirm({
                title: 'Refresh Demo Domain',
                icon: <WarningOutlined />,
                okText: 'Refresh',
                content: (
                    <p>
                        Are you sure you want to refresh <b>{domain.display_name}</b>? All existing content will be
                        replaced with a fresh clone from the template. This cannot be undone.
                    </p>
                ),
                onOk: async () => {
                    const { ok } = await this.domainService.recloneDomain(domain.id, { showLoadingScreen: true })
                    if (ok) {
                        return this.fetchDomains()
                    }
                },
            } as any)
        } else if (key === 'reseed-stats') {
            Modal.confirm({
                title: 'Refresh Demo Stats',
                icon: <BarChartOutlined />,
                okText: 'Refresh Stats',
                content: (
                    <p>
                        Regenerate synthetic stats for <b>{domain.display_name}</b>? Existing stats will be replaced
                        with fresh data. Content and settings are not affected.
                    </p>
                ),
                onOk: async () => {
                    await this.domainService.reseedDomainStats(domain.id, { showLoadingScreen: true })
                },
            } as any)
        } else if (key === 'clone-demo') {
            this.setState({
                newDomainModalMode: 'demo',
                newDomainModalInitialTemplateId: domain.source_demo_template_domain_id ?? undefined,
                newDomainModalInitialValues: domain,
            })
        } else if (key === 'generate-test-domain') {
            this.showNewTestDomainPrompt(domain)
        } else if (key === 'archive-demo') {
            Modal.confirm({
                title: 'Archive Demo Domain',
                icon: <WarningOutlined />,
                okText: 'Archive',
                okButtonProps: { danger: true },
                onOk: () => this.handleArchiveDemoDomain(domain),
                content: (
                    <p>
                        Are you sure you want to archive <b>{domain.display_name}</b>? This action cannot be undone.
                    </p>
                ),
            } as any)
        } else if (key === 'disable') {
            const formRef = React.createRef<FormInstance>()
            Modal.confirm({
                title: 'Disable Domain',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current
                    if (!!form) {
                        await form.validateFields()
                        return this.handleDisable(domain)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to disable this domain?</p>
                        <p>
                            Please type the Domain name, <b>{domain.name}</b>, to continue with this action.
                        </p>
                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback('Account name is required.')
                                        } else if (value !== domain.name) {
                                            callback('Account name does not match.')
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'enable') {
            Modal.confirm({
                title: 'Enable Domain',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => this.handleEnable(domain),
                content: <p>Are you sure you want to enable this domain?</p>,
            } as any)
        }
    }

    protected handleArchiveDemoDomain = async (domain: DomainDto) => {
        const { ok } = await this.domainService.destroy(domain.id, { showLoadingScreen: true })
        if (ok) {
            return this.fetchDomains()
        }
    }

    protected handleDisable = async (domain: DomainDto) => {
        await this.domainService.patch(domain.id, { status: 'DISABLED' })
        return this.fetchDomains()
    }

    protected handleEnable = async (domain: DomainDto) => {
        await this.domainService.patch(domain.id, { status: 'ACTIVE' })
        return this.fetchDomains()
    }

    // Status and Type are applied together so one apply is one fetch and one response.
    protected applyFilters = (statusFilter: string[], typeFilter: DomainTypeFilter[]) => {
        if (deepEqual(statusFilter, this.state.statusFilter) && deepEqual(typeFilter, this.state.typeFilter)) {
            return
        }
        this.setState({ statusFilter, typeFilter, page: 1 }, () => this.fetchDomains())
    }

    protected searchDomains = async (value: string): Promise<void> => {
        let query = value.trim()
        if (value.length < 2) {
            query = undefined
        }
        await this.setState({ query })
        return this.fetchDomains()
    }

    protected async fetchDomains(): Promise<void> {
        let update: any = { loading: true }
        await this.setState(update)

        update.loading = false
        update.domains = []

        const domainFields = [
            'id',
            'prod_domain_id',
            'name',
            'display_name',
            'account_name',
            'account_id',
            'status',
            'status_id',
            'flags',
            'is_demo',
            'is_demo_template',
            'source_demo_template_domain_id',
            'timezone',
            'currency_code',
            'industry_id',
            'default_icon_url',
        ]

        let response: IApiResponse<DomainDto[]> = { ok: false, data: [] }
        const options = {
            showLoadingScreen: false,
            query: {
                page: this.state.page,
                limit: this.state.pageSize,
                search: this.state.query,
                status: this.state.statusFilter.join(','),
                fields: domainFields.join(','),
                ...(this.state.typeFilter.length > 0 && this.state.typeFilter.length < 3
                    ? { type: this.state.typeFilter.join(',') }
                    : {}),
            },
        }

        if (this.props.options.account?.id) {
            response = await this.accountService.fetchDomains(this.props.options.account?.id, options)
        } else {
            response = await this.domainService.fetchAll(options)
        }

        if (response && response.ok) {
            update.domains = response.data
            update.paginationLinks = response.meta.links ?? this.state.paginationLinks
            update.domainsMeta = response.meta
        }

        return this.setState(update)
    }
}
